import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Settings,
  CreditCard,
  Printer,
  ScrollText,
  Store,
  Users,
  Bell,
  Shield,
  Palette,
  Search,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Save,
  RefreshCw,
  Loader2,
  Plus,
  TestTube2,
  Building2,
  Cpu,
  CheckCircle,
  AlertCircle,
  PanelLeftClose,
  PanelLeft,
  BringToFront,
  Banknote,
  Key,
  CalendarClock
} from 'lucide-react';

// Import actual settings components
import BranchSettings from './BranchSettings';
import SettingsManagement from './SettingsManagement';
import PrinterConfigurations from './PrinterConfigurations';
import ReceiptConfigurations from './ReceiptConfigurations';
import IntegrationMessages from './Integration_Setting/IntegrationMessages';
import NotificationSettings from './Notification_Setting/NotificationSetting';
import TaxManagement from './Tax/TaxView';
import ResetPassword from './ResetPassword/resetPassword';
import DayCloseSettings from './DayCloseSettings';
import { useAuth } from '@/context/AuthContext';
import { P } from '@/config/permissionCodes';

const SETTINGS_GROUPS = [
  {
    id: 'business',
    title: 'Business',
    icon: Building2,
    items: [
      {
        id: 'branch',
        title: 'Branch Information',
        icon: Store,
        enabled: true,
        component: BranchSettings,
        permissionCode: P.BACK_OFFICE.SETTINGS.BRANCH,
        keywords: ['restaurant', 'name', 'address', 'gstin', 'fssai', 'logo', 'contact', 'service', 'mode', 'dine-in', 'counter'],
      },
      {
        id: 'payments',
        title: 'Payment Accounts',
        icon: CreditCard,
        enabled: true,
        component: SettingsManagement,
        permissionCode: P.BACK_OFFICE.SETTINGS.PAYMENTS,
        keywords: ['upi', 'bank', 'payment', 'card', 'cash', 'online'],
      },
      {
        id: 'day-close',
        title: 'Closeout Config',
        icon: CalendarClock,
        enabled: true,
        component: DayCloseSettings,
        permissionCode: P.BACK_OFFICE.SETTINGS.DAY_CLOSE,
        keywords: ['day', 'close', 'closeout', 'business', 'date', 'shift', 'end', 'config'],
      },
    ],
  },
  {
    id: 'hardware',
    title: 'Hardware',
    icon: Cpu,
    items: [
      {
        id: 'printers',
        title: 'Printers',
        icon: Printer,
        enabled: true,
        component: PrinterConfigurations,
        permissionCode: P.BACK_OFFICE.SETTINGS.PRINTERS,
        keywords: ['printer', 'bill', 'kot', 'thermal', 'print'],
      },
      {
        id: 'receipts',
        title: 'Receipt Templates',
        icon: ScrollText,
        enabled: true,
        component: ReceiptConfigurations,
        permissionCode: P.BACK_OFFICE.SETTINGS.RECEIPTS,
        keywords: ['receipt', 'template', 'bill', 'kot', 'format'],
      },
    ],
  },
  {
    id: 'access',
    title: 'Access Control',
    icon: Shield,
    items: [
      {
        id: 'users',
        title: 'Users & Roles',
        icon: Users,
        enabled: false,
        keywords: ['user', 'staff', 'role', 'permission'],
      },
      {
        id: 'security',
        title: 'Security',
        icon: Shield,
        enabled: false,
        keywords: ['security', 'password', 'session'],
      },
      {
        id: 'reset-password',
        title: 'Reset Password',
        icon: Key,
        enabled: true,
        component: ResetPassword,
        permissionCode: P.BACK_OFFICE.SETTINGS.RESET_PASSWORD,
        keywords: ['password', 'reset', 'security', 'credentials'],
      }
    ],
  },
  {
    id: 'preferences',
    title: 'Preferences',
    icon: Palette,
    items: [
      {
        id: 'notifications',
        title: 'Notifications',
        icon: Bell,
        enabled: true,
        component: NotificationSettings,
        permissionCode: P.BACK_OFFICE.SETTINGS.NOTIFICATIONS,
        keywords: ['notification', 'alert', 'sound'],
      },
      {
        id: 'integrations',
        title: 'Integrations',
        icon: BringToFront,
        enabled: true,
        component: IntegrationMessages,
        permissionCode: P.BACK_OFFICE.SETTINGS.INTEGRATIONS,
        keywords: ['integration'],
      },
      {
        id: 'appearance',
        title: 'Appearance',
        icon: Palette,
        enabled: false,
        keywords: ['theme', 'dark', 'light', 'color'],
      },
    ],
  },
  {
    id: 'finance',
    title: 'Finance',
    icon: Banknote,
    items: [
      {
        id: 'tax',
        title: 'Tax Rates',
        icon: Banknote,
        enabled: true,
        component: TaxManagement,
        permissionCode: P.BACK_OFFICE.SETTINGS.TAX,
        keywords: ['tax', 'gst', 'vat', 'cgst', 'sgst', 'rate'],
      },
    ],
  },
];

// Flatten items for search — done dynamically after permission filtering (see below)
const ALL_ITEMS = SETTINGS_GROUPS.flatMap(g => g.items);


function SearchBar({ value, onChange }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search..."
        className="w-full pl-9 pr-3 py-2 bg-slate-100 border-0 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all"
      />
    </div>
  );
}

function AccordionSection({ group, expandedGroups, toggleGroup, activeItem, onItemClick, searchQuery, isCollapsed }) {
  const GroupIcon = group.icon;
  const isExpanded = expandedGroups.includes(group.id);
  const { hasPermission } = useAuth();

  // Filter items: permission check + search query
  const visibleItems = group.items.filter(item => {
    // Permission gate — if permissionCode is set, user must have it
    if (item.permissionCode && !hasPermission(item.permissionCode)) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.title.toLowerCase().includes(query) ||
        item.keywords?.some(k => k.includes(query))
      );
    }

    return true;
  });

  const hasActiveItem = visibleItems.some(item => item.id === activeItem);

  // Don't show group if no items are visible
  if (visibleItems.length === 0) return null;

  // Collapsed mode — show only icons
  if (isCollapsed) {
    return (
      <div className="py-1">
        {visibleItems.map((item) => {
          const ItemIcon = item.icon;
          const isActive = activeItem === item.id;
          return (
            <button
              key={item.id}
              onClick={() => item.enabled && onItemClick(item.id)}
              disabled={!item.enabled}
              title={item.title}
              className={`
                w-full flex items-center justify-center py-2.5 transition-colors relative
                ${isActive ? 'bg-teal-100 text-teal-600' : 'hover:bg-slate-50 text-slate-500'}
                ${!item.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-teal-600 rounded-r-full" />
              )}
              <ItemIcon size={18} />
            </button>
          );
        })}
      </div>
    );
  }

  // Expanded mode — full accordion
  return (
    <div className="border-b border-slate-100 last:border-0">
      {/* Section Header */}
      <button
        onClick={() => toggleGroup(group.id)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
          ${hasActiveItem ? 'bg-teal-50' : 'hover:bg-slate-50'}
        `}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${hasActiveItem ? 'bg-teal-100' : 'bg-slate-100'}`}>
          <GroupIcon size={14} className={hasActiveItem ? 'text-teal-600' : 'text-slate-500'} />
        </div>
        <span className={`flex-1 text-sm font-medium ${hasActiveItem ? 'text-teal-900' : 'text-slate-700'}`}>
          {group.title}
        </span>
        {isExpanded ? (
          <ChevronDown size={16} className="text-slate-400" />
        ) : (
          <ChevronRight size={16} className="text-slate-400" />
        )}
      </button>

      {/* Section Items */}
      {(isExpanded || searchQuery) && (
        <div className="pb-2">
          {visibleItems.map((item) => {
            const ItemIcon = item.icon;
            const isActive = activeItem === item.id;
            return (
              <button
                key={item.id}
                onClick={() => item.enabled && onItemClick(item.id)}
                disabled={!item.enabled}
                className={`
                  w-full flex items-center gap-3 pl-14 pr-4 py-2 text-left transition-colors
                  ${isActive ? 'bg-teal-100 text-teal-900' : 'hover:bg-slate-50 text-slate-600'}
                  ${!item.enabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                <ItemIcon size={14} className={isActive ? 'text-teal-600' : 'text-slate-400'} />
                <span className="flex-1 text-sm">{item.title}</span>
                {!item.enabled && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 text-slate-500 rounded-full flex items-center gap-0.5">
                    <Sparkles size={8} />
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HeaderActions({ activeItem, componentActions }) {
  if (!componentActions) return null;

  // Branch Settings actions
  if (activeItem === 'branch') {
    if (componentActions.isEditing) {
      return (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
            <AlertCircle size={12} />
            Editing
          </span>
          <button
            onClick={componentActions.onCancel}
            disabled={componentActions.saving}
            className="px-3 py-1.5 text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-medium disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={componentActions.onSave}
            disabled={componentActions.saving}
            className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
          >
            {componentActions.saving ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} />
                Save Changes
              </>
            )}
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onEdit}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Settings size={14} />
          Edit Settings
        </button>
      </div>
    );
  }

  // Printers actions
  if (activeItem === 'printers' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        {componentActions.onTestBill && (
          <button
            onClick={componentActions.onTestBill}
            disabled={componentActions.testingPrinter === "BILL"}
            className="px-2.5 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
          >
            <TestTube2 size={14} />
            Test Bill
          </button>
        )}
        {componentActions.onTestKOT && (
          <button
            onClick={componentActions.onTestKOT}
            disabled={componentActions.testingPrinter === "KOT"}
            className="px-2.5 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
          >
            <TestTube2 size={14} />
            Test KOT
          </button>
        )}
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Printer
        </button>
      </div>
    );
  }

  // Receipts actions
  if (activeItem === 'receipts' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          New Template
        </button>
      </div>
    );
  }

  // Payment Accounts actions
  if (activeItem === 'payments' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Account
        </button>
      </div>
    );
  }

  // Tax actions
  if (activeItem === 'tax' && componentActions.onAdd) {
    return (
      <div className="flex items-center gap-2">
        {componentActions.onRefresh && (
          <button
            onClick={componentActions.onRefresh}
            className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
          >
            <RefreshCw size={16} />
          </button>
        )}
        <button
          onClick={componentActions.onAdd}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={14} />
          Add Tax
        </button>
      </div>
    );
  }

  // Day Close actions
  if (activeItem === 'day-close' && componentActions.onSave) {
    return (
      <div className="flex items-center gap-2">
        {componentActions.hasChanges && (
          <span className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
            <AlertCircle size={12} />
            Unsaved
          </span>
        )}
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
        <button
          onClick={componentActions.onSave}
          disabled={componentActions.saving || !componentActions.hasChanges}
          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium disabled:opacity-50"
        >
          {componentActions.saving ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save size={14} />
              Save
            </>
          )}
        </button>
      </div>
    );
  }

  // Notifications actions
  if (activeItem === 'notifications' && componentActions.onRefresh) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={componentActions.onRefresh}
          className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
        >
          <RefreshCw size={16} />
        </button>
      </div>
    );
  }

  // Integrations actions
  if (activeItem === 'integrations') {
    if (componentActions.selectedTab === 'sms' && componentActions.onAddTemplate) {
      return (
        <div className="flex items-center gap-2">
          {componentActions.onRefresh && (
            <button
              onClick={componentActions.onRefresh}
              className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
            >
              <RefreshCw size={16} />
            </button>
          )}
          <button
            onClick={componentActions.onAddTemplate}
            className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
          >
            <Plus size={14} />
            Add Template
          </button>
        </div>
      );
    }
    if (componentActions.onAddProvider) {
      return (
        <div className="flex items-center gap-2">
          {componentActions.onRefresh && (
            <button
              onClick={componentActions.onRefresh}
              className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
            >
              <RefreshCw size={16} />
            </button>
          )}
          <button
            onClick={componentActions.onAddProvider}
            className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
          >
            <Plus size={14} />
            Add Provider
          </button>
        </div>
      );
    }
  }

  return null;
}

function AccessDeniedContent() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Shield size={28} className="text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
        <p className="text-sm text-slate-500">You don't have permission to view this section.</p>
      </div>
    </div>
  );
}

function ComingSoonContent() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Sparkles size={28} className="text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Coming Soon</h3>
        <p className="text-sm text-slate-500">This feature is under development.</p>
      </div>
    </div>
  );
}

export default function SettingsHubV5() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(['business', 'hardware']);
  const [componentActions, setComponentActions] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ── Screen-level permission guard ──────────────────────────────────────────
  if (!hasPermission(P.BACK_OFFICE.SETTINGS.SCREEN)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center py-20">
          
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
          <p className="text-sm text-slate-500">You don't have permission to view settings.</p>
        </div>
      </div>
    );
  }

  // Get active item from URL, default to first permitted item
  const tabFromUrl = searchParams.get('tab');
  const validTabs = ALL_ITEMS.map(item => item.id);
  const activeItem = validTabs.includes(tabFromUrl) ? tabFromUrl : 'branch';

  // Check if the active item's permission is allowed
  const activeMenuItem = ALL_ITEMS.find(item => item.id === activeItem);
  const activeItemPermitted = !activeMenuItem?.permissionCode || hasPermission(activeMenuItem.permissionCode);

  const ActiveComponent = activeItemPermitted ? activeMenuItem?.component : null;

  // Handle item click — update URL
  const handleItemClick = (itemId) => {
    setSearchParams({ tab: itemId }, { replace: true });
  };

  // Ensure the group containing the active item is expanded
  useEffect(() => {
    const activeGroup = SETTINGS_GROUPS.find(g =>
      g.items.some(item => item.id === activeItem)
    );
    if (activeGroup && !expandedGroups.includes(activeGroup.id)) {
      setExpandedGroups(prev => [...prev, activeGroup.id]);
    }
  }, [activeItem]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  const handleActionsReady = useCallback((actions) => {
    setComponentActions(actions);
  }, []);

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Sidebar Menu (Collapsible) */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-white border-r border-slate-200 flex flex-col transition-all duration-300`}>
        {/* Header */}
        <div className={`${sidebarCollapsed ? 'p-2' : 'p-4'} border-b border-slate-200`}>
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Settings size={16} className="text-white" />
              </div>
              <button
                onClick={toggleSidebar}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                title="Expand Sidebar"
              >
                <PanelLeft size={18} />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                  <Settings size={16} className="text-white" />
                </div>
                <h1 className="text-lg font-bold text-slate-900 flex-1">Settings</h1>
                <button
                  onClick={toggleSidebar}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
                  title="Collapse Sidebar"
                >
                  <PanelLeftClose size={18} />
                </button>
              </div>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
            </>
          )}
        </div>

        {/* Accordion Sections */}
        <div className="flex-1 overflow-y-auto">
          {SETTINGS_GROUPS.map((group) => (
            <AccordionSection
              key={group.id}
              group={group}
              expandedGroups={expandedGroups}
              toggleGroup={toggleGroup}
              activeItem={activeItem}
              onItemClick={handleItemClick}
              searchQuery={searchQuery}
              isCollapsed={sidebarCollapsed}
            />
          ))}
        </div>
      </div>

      {/* Right Panel - Content */}
      <div className="flex-1 bg-slate-50 overflow-hidden flex flex-col">
        {/* Content Header with Actions */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-lg font-bold text-slate-900">{activeMenuItem?.title}</h1>
          {/* Only show header actions if user has permission for this item */}
          {activeItemPermitted && (
            <HeaderActions activeItem={activeItem} componentActions={componentActions} />
          )}
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto">
          {!activeItemPermitted ? (
            <AccessDeniedContent />
          ) : activeMenuItem?.enabled && ActiveComponent ? (
            <ActiveComponent renderActions={handleActionsReady} />
          ) : (
            <ComingSoonContent />
          )}
        </div>
      </div>
    </div>
  );
}